import React, { FC, useEffect, useMemo, useState } from "react";
import {
  Form,
  Input,
  InputNumber,
  Button,
  Select,
  Switch,
  Tooltip,
  Divider,
  Typography,
} from "antd";
import { OrganisationSettingsFormValues, Settings, TAX_LABEL_VALUES } from "../../data-types";
import styles from "./organisation-settings.module.css";
import { NodeIndexOutlined, InfoCircleOutlined } from "@ant-design/icons";

const { Title } = Typography;

const {
  ["container"]: containerClassName,
  ["form"]: formClassName,
  ["button-row"]: buttonRowClassName,
  ["submit-button"]: submitButton,
} = styles;

export type OrganisationSettingsProps = {
  settings: Partial<Settings>;
  onUpdate: (formValues: OrganisationSettingsFormValues) => Promise<void>;
  loading: boolean;
  currencies: { id: number; code: string; symbol: string }[];
};

export const OrganisationSettings: FC<OrganisationSettingsProps> = ({
  settings,
  onUpdate,
  loading: dataLoading,
  currencies,
}) => {
  const {
    gpmDangerThreshold,
    gpmWarningThreshold,
    organisationPmsName,
    taxLevelPercent,
    taxLabel,
    administrationCharge,
    currency,
    decimalTimeInput,
    autoCloseIntervalInDays,
    taxEnabled,
    taxEnabledOptionDefault,
  } = settings;

  const initialValues = useMemo(() => {
    return {
      gpmDangerThreshold,
      gpmWarningThreshold,
      organisationPmsName,
      taxLevelPercent,
      taxLabel,
      administrationCharge,
      currency: currency?.id,
      decimalTimeInput,
      autoCloseIntervalInDays,
      taxEnabled,
      taxEnabledOptionDefault,
    };
  }, [
    gpmDangerThreshold,
    gpmWarningThreshold,
    organisationPmsName,
    taxLevelPercent,
    taxLabel,
    administrationCharge,
    currency,
    autoCloseIntervalInDays,
    taxEnabled,
    taxEnabledOptionDefault,
  ]);

  const [form] = Form.useForm();
  const [isTaxEnabled, setIsTaxEnabled] = useState<boolean>(taxEnabled ?? true);

  const onCancel = () => {
    form.resetFields();
  };

  const [formLoading, setFormLoading] = useState<boolean>(false);

  const loading = formLoading || dataLoading;

  const doOnFinish = async (formValues: OrganisationSettingsFormValues) => {
    console;
    try {
      setFormLoading(true);
      const values = {
        administrationCharge: formValues.administrationCharge,
        autoCloseIntervalInDays: formValues.autoCloseIntervalInDays,
        decimalTimeInput: formValues.decimalTimeInput,
        gpmDangerThreshold: formValues.gpmDangerThreshold,
        gpmWarningThreshold: formValues.gpmWarningThreshold,
        organisationPmsName: formValues.organisationPmsName,
        currency: formValues.currency,
        taxEnabled: formValues.taxEnabled,
        taxLabel: formValues.taxLabel,
        taxLevelPercent: formValues.taxLevelPercent,
        taxEnabledOptionDefault: formValues.taxEnabledOptionDefault,
      };

      await onUpdate(values);
    } finally {
      setFormLoading(false);
    }
  };

  useEffect(() => {
    form.setFieldsValue(initialValues);
    form.validateFields();
    setIsTaxEnabled(taxEnabled ?? true);
  }, [form, initialValues, taxEnabled]);

  return (
    <div className={containerClassName}>
      <Form
        initialValues={initialValues}
        form={form}
        labelAlign="left"
        labelCol={{ span: 12 }}
        wrapperCol={{ span: 17, offset: 1 }}
        className={formClassName}
        onFinish={doOnFinish}
      >
        <Form.Item
          label="GPM Required"
          name="gpmDangerThreshold"
          rules={[
            {
              required: true,
              message: "Please enter the Gross Profit Margin (GPM) Danger threshold",
            },
          ]}
        >
          <InputNumber
            min={0}
            max={9999999}
            disabled={loading}
            formatter={(value) => `${value}%`}
            parser={(value: any) => value.replace("%", "")}
          />
        </Form.Item>

        <Form.Item
          label="GPM Warning threshold"
          name="gpmWarningThreshold"
          rules={[
            {
              required: true,
              message: "Please enter the Gross Profit Margin (GPM) Warning threshold",
            },
          ]}
        >
          <InputNumber
            min={0}
            max={100}
            disabled={loading}
            formatter={(value) => `${value}%`}
            parser={(value: any) => value.replace("%", "")}
          />
        </Form.Item>
        <Form.Item
          label="PMS Name"
          name="organisationPmsName"
          rules={[
            {
              required: true,
              message: "Please enter the name of your Practice Management System (PMS)",
            },
          ]}
        >
          <Input placeholder="PMS" prefix={<NodeIndexOutlined />} disabled={loading} />
        </Form.Item>
        <Form.Item label="Administration Charge" name="administrationCharge">
          <InputNumber<number>
            min={0}
            max={100}
            disabled={loading}
            formatter={(value) => `${value}%`}
            parser={(value: any) => value.replace("%", "")}
          />
        </Form.Item>
        <Form.Item label="Currency" name="currency">
          <Select
            options={currencies.map((c) => {
              return {
                value: c.id,
                label: c.code,
              };
            })}
            defaultValue={"GBP"}
          />
        </Form.Item>
        <Form.Item label="Decimal Time Inputs" name="decimalTimeInput" valuePropName="checked">
          <Switch
            disabled={loading}
            onChange={(checked) => form.setFieldsValue({ decimalTimeInput: checked })}
          />
        </Form.Item>
        <Form.Item
          label={
            <>
              Automatically mark matters as closed after
              <Tooltip
                title={`VPD and ${organisationPmsName} matters will be considered closed if no associated time entries have been created in this time. Only closed matters will appear in historical matter search results.`}
              >
                <InfoCircleOutlined style={{ marginLeft: 5 }} />
              </Tooltip>
            </>
          }
          name="autoCloseIntervalInDays"
        >
          <InputNumber<number>
            style={{ width: "100px" }}
            min={0}
            disabled={loading}
            formatter={(value) => `${value} days`}
            parser={(value: any) => value.replace(" days", "")}
          />
        </Form.Item>

        <Divider orientation="left">
          <Title level={5} style={{ margin: 0, color: "var(--heading-color)" }}>
            Tax Settings
          </Title>
        </Divider>

        <Form.Item
          label={
            <>
              Tax Enabled
              <Tooltip title="When enabled, tax will be available across the entire application. Disabling this will remove tax functionality site-wide.">
                <InfoCircleOutlined style={{ marginLeft: 5 }} />
              </Tooltip>
            </>
          }
          name="taxEnabled"
          valuePropName="checked"
        >
          <Switch
            disabled={loading}
            onChange={(checked) => {
              setIsTaxEnabled(checked);
              form.setFieldsValue({ taxEnabled: checked });
              if (!checked) {
                form.setFieldsValue({ taxEnabledOptionDefault: false });
              }
            }}
          />
        </Form.Item>

        {isTaxEnabled && (
          <>
            <Form.Item
              label="Tax Amount"
              name="taxLevelPercent"
              rules={[
                {
                  required: isTaxEnabled,
                  message: "Please enter the Tax Amount percentage",
                },
              ]}
            >
              <InputNumber<number>
                min={0}
                max={100}
                disabled={loading}
                formatter={(value) => `${value}%`}
                parser={(value: any) => value.replace("%", "")}
              />
            </Form.Item>
            <Form.Item label="Tax Label" name="taxLabel">
              <Select
                options={TAX_LABEL_VALUES.map((t) => {
                  return {
                    value: t,
                    label: t,
                  };
                })}
                defaultValue={"VAT"}
                disabled={loading}
              />
            </Form.Item>
            <Form.Item
              label={
                <>
                  Tax Exempt by Default for New Matters
                  <Tooltip title="When enabled, newly created matters will automatically have tax enabled. Users can still toggle this per matter.">
                    <InfoCircleOutlined style={{ marginLeft: 5 }} />
                  </Tooltip>
                </>
              }
              name="taxEnabledOptionDefault"
              valuePropName="checked"
            >
              <Switch
                disabled={loading}
                onChange={(checked) => form.setFieldsValue({ taxEnabledOptionDefault: checked })}
              />
            </Form.Item>
          </>
        )}

        <Form.Item shouldUpdate className={buttonRowClassName}>
          {() => {
            const submitDisabled =
              loading || form.getFieldsError().filter(({ errors }) => errors.length).length > 0;
            return (
              <>
                <Button onClick={onCancel} disabled={loading}>
                  Reset
                </Button>
                <Button
                  className={submitButton}
                  type="primary"
                  htmlType="submit"
                  disabled={submitDisabled}
                >
                  Update Organisation Settings
                </Button>
              </>
            );
          }}
        </Form.Item>
      </Form>
    </div>
  );
};
